import { PrimaryButton } from "@/components/button/primary-button";
import { useGomakeAxios } from "@/hooks/use-gomake-axios";
import { useSnackBar } from "@/hooks/use-snack-bar";
import { selectedClientState } from "@/pages-components/quotes/states";
import { FONT_FAMILY } from "@/utils/font-family";
import { isUsPrintHouseState } from "@/store/print-house-profile";
import {
  buildCustomerRequiredFieldRules,
  formatCustomerMissingRequiredFieldsAlert,
  getMissingContactFirstNames,
  resolveUsesUsCountryStateAddressForm,
  type PartyRequiredFieldPreferenceScope,
} from "@/utils/customer-required-fields";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { prevSelectedClientState } from "@/pages-components/admin/home/widgets/quote-widget/states";
import { systemCurrencyState } from "@/store/user";
import { resetPassModalState } from "./state";
import {
  clientGroupsCategoriesState,
  clientTypesCategoriesState,
  paymentTermsCustomerState,
  paymentTermsSupplierState,
} from "@/pages/customers/customer-states";
import { getAllPaymentTermsApi } from "@/services/api-service/customers/payment-terms-api";
import { gomakeUserState } from "./components/gomakeUser-tab/gomakeUserState";
import { useEditCustomer } from "@/pages/customers/edit-customer/use-edit-customer";
import { useAddCustomer } from "@/pages/customers/add-customer/use-add-customer";
import { CLIENT_TYPE, CLIENT_TYPE_Id } from "@/pages/customers/enums";
import { emailRegex } from "@/utils/regex";
import { useUserProfile } from "@/hooks/use-user-profile";
import { getClientsGroupsApi } from "@/services/api-service/suppliers/suppliers-endpoints";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { isValidCustomer } from "@/utils/helpers";
import { useUserPermission } from "@/hooks/use-permission";
import { Permissions } from "@/components/CheckPermission/enum";
import { userProfileState } from "@/store/user-profile";
import { printHouseCountryState } from "@/store/print-house-country";
import { EPreferences } from "@/enums/prefernces";
import { getAllShipmentTypesApi } from "@/services/api-service/shipmentTypes/shipment-type-api";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useTheme } from "@/contexts/theme";
import { useDefaultLanguageOptions } from "@/hooks/use-default-language-options";
import { EAddressType } from "@/widgets/quote-new/business-widget/address-widget/state";
import { getAllProductsMongoDB } from "@/services/hooks/admin-side/products/get-all-products-mongodb";
import { printHousePreferencesState } from "@/store/print-house-preferences";

const useCustomerCard = ({
  t,
  customer,
  setCustomer,
  onClose,
  setOpenOfferModal,
  userQuote,
  typeClient,
  getAllCustomers,
  onCustomerAdd,
  isgetAllCustomers,
  isFromCartPage,
  onChangeSelectBusiness,
  openModal,
  isFromHomePage = false,
}) => {
  const similarCustomerSearchKeys = ["name", "mail", "phone", "tel1"];
  const { callApi } = useGomakeAxios();
  const { primaryColor, secondColor: secondaryColor } = useGomakeTheme();
  const [showTable, setShowTable] = useState(false);
  const [customerTableRows, setCustomerTableRows] = useState([]);
  const [showInActiveCustomers, setShowInActiveCustomers] =
    useState<boolean>(false);
  const [selectedClient, setSelectedClient] =
    useRecoilState(selectedClientState);
  const { alertFault } = useSnackBar();
  const systemCurrency = useRecoilValue<any>(systemCurrencyState);
  const [resetPassModal, setResetPassModalModal] =
    useRecoilState<boolean>(resetPassModalState);
  const clientTypesCategories = useRecoilValue(clientTypesCategoriesState);
  const gomakeUser = useRecoilValue<any>(gomakeUserState);
  const { defaultLanguageOptions } = useDefaultLanguageOptions();
  const { CheckPreferenceValue, GetPreferenceKey } = usePrintHousePreferences();
  const { CheckPermission } = useUserPermission();
  const printHouseCountry = useRecoilValue<string>(printHouseCountryState);
  const [shipmentTypes, setShipmentTypes] = useState<
    { value: string; text: string }[]
  >([]);
  const [productsList, setProductsList] = useState<
    { id: string; name: string }[]
  >([]);

  const { currentTheme } = useTheme();
  const isHp = currentTheme === "hp-client";
  const customerTableHeaders = [
    t("customers.customerCode"),
    t("customers.name"),
    t("customers.email"),
    t("customers.phone"),
    t("customers.status"),
    ...(isFromHomePage ? [isHp ? "" : t("home.headers.more")] : []),
  ];
  const customerTableColumnWidths =
    isHp && isFromHomePage
      ? [undefined, undefined, undefined, undefined, undefined, "1%"]
      : undefined;
  const profileState = useRecoilValue(userProfileState);
  const printHousePrefs = useRecoilValue<any>(printHousePreferencesState);
  const partyRequiredFieldScope: PartyRequiredFieldPreferenceScope =
    typeClient === CLIENT_TYPE.SUPPLIER ? "supplier" : "customer";
  const customerRequiredFieldRules = useMemo(
    () =>
      buildCustomerRequiredFieldRules(
        printHousePrefs ?? [],
        partyRequiredFieldScope,
      ),
    [printHousePrefs, partyRequiredFieldScope],
  );
  const isUsPrintHouse = useRecoilValue(isUsPrintHouseState);
  const addressFormIsUs = resolveUsesUsCountryStateAddressForm(
    isUsPrintHouse,
    !!CheckPreferenceValue(EPreferences.ADDRESSES_WITH_COUNTRY_AND_STATE),
  );

  const handleHideTable = () => setShowTable(false);

  const [previousClient, setPreviousClient] = useRecoilState<any>(
    prevSelectedClientState,
  );

  const handleOpenModal = (newClient) => {
    //setPreviousClient(selectedClient ? selectedClient : previousClient );
    setPreviousClient(selectedClient);
    setSelectedClient(newClient);
    setOpenOfferModal(true);
  };

  const handleChooseCustomer = (customer) => {
    if (
      userQuote?.client?.id != null &&
      customer?.id != null &&
      userQuote?.client?.id !== customer?.id
    ) {
      handleOpenModal(customer);
    } else {
      setSelectedClient(customer);
    }
    onClose();
    handleHideTable();
    setCustomer(null);
  };

  const getAllShipmentTypes = async () => {
    const callBack = (res) => {
      if (res?.success) {
        let types = res?.data.map((item) => ({
          value: item.id,
          text: item.title,
        }));

        setShipmentTypes(types);
      }
    };

    await getAllShipmentTypesApi(callApi, callBack);
  };

  const mapCustomerData = (customer) => {
    const { code, name, mail, phone, isActive } = customer;
    const statusText = isActive
      ? t("usersSettings.active")
      : t("usersSettings.inactive");
    const statusColor = isActive ? "#40CC4E" : "#D92C2C";

    const rowData = [
      code,
      name,
      mail,
      phone,
      <div
        style={{
          display: "inline-flex",
          ...FONT_FAMILY.Lexend(500, 14),
          color: statusColor,
        }}
      >
        {statusText}
      </div>,
    ];

    if (isFromHomePage) {
      rowData.push(
        <PrimaryButton
          onClick={() => handleChooseCustomer(customer)}
          variant="outlined"
          style={{ width: "fit-content", height: "fit-content" }}
        >
          {t("datepicker.choose")}
        </PrimaryButton>,
      );
    }

    return rowData;
  };

  const getAllSimilarCustomersData = useCallback(() => {
    let customersArray = [...customerTableRows];
    if (customersArray?.length > 0) {
      return showInActiveCustomers
        ? customersArray
        : customersArray?.filter((user: any) => user.isActive);
    }
    return customersArray;
  }, [customerTableRows, showInActiveCustomers]);

  const onShowInActiveCustomers = (value: boolean) => {
    setShowInActiveCustomers(value);
  };

  const [open, setOpen] = useState(false);
  const validateEmail = (state: any, fieldName: string) =>
    !!state?.[fieldName] ? emailRegex.test(state?.[fieldName]) : true;
  const normalizeContactName = (value: any) =>
    String(value ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  const hasDuplicateMainContactName = (
    customerData: any,
    contactsList?: any[],
  ) => {
    const mainContactName = normalizeContactName(customerData?.mainContactName);

    if (!mainContactName) {
      return false;
    }

    return !!contactsList?.some((contact) => {
      const contactFullName = normalizeContactName(
        `${contact?.firstName ?? ""} ${contact?.lastName ?? ""}`,
      );

      return !!contactFullName && contactFullName === mainContactName;
    });
  };
  const { addNewCustomer } = useAddCustomer();
  const { editCustomer } = useEditCustomer();
  const { updateUserPassword } = useUserProfile();

  // in order to avoid sending an empty object that include just name & index

  const isNameIndexOnly = (dataObject) => {
    const { name, index, ...otherProps } = dataObject;

    // const processedPhone = clearPhoneNumberPrefix(phone);

    // const processedTel1 = clearPhoneNumberPrefix(tel1);

    // const processedTel2 = clearPhoneNumberPrefix(tel2);

    // const isPhoneEmpty =
    //   !processedPhone ||
    //   !processedTel1 ||
    //   !processedTel2 ||
    //   [processedPhone, processedTel1, processedTel2].some(
    //     (field) => field === null || field === undefined || field === ""
    //   );

    const emptyProps = Object.values(otherProps)?.every(
      (prop) => prop === null || prop === "" || prop === " ",
    );

    return emptyProps;
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
    setCustomer(null);
    handleHideTable();
    setCustomerTableRows([]);
    setShowInActiveCustomers(false);
  };

  const clientTypeLabel =
    typeClient === "C"
      ? t("customers.modal.clientType")
      : t("suppliers.supplierType");

  const clientTypeId =
    typeClient === "C" ? CLIENT_TYPE_Id.CUSTOMER : CLIENT_TYPE_Id.SUPPLIER;

  // Match Contacts tab UI: main contacts are not editable there — validate only editable rows.
  // Still keep main contacts in the save payload.
  const getContactsForSave = () =>
    contacts?.filter((contact) => !isNameIndexOnly(contact));
  const getContactsForValidation = (contactsForSave?: any[]) =>
    (contactsForSave ?? getContactsForSave())?.filter(
      (contact) => !contact?.isMainContact,
    );

  const handleAddCustomer = async () => {
    const filteredContacts = getContactsForSave();
    const contactsForValidation = getContactsForValidation(filteredContacts);
    const filteredAddresses = addresses?.filter(
      (address) => !isNameIndexOnly(address),
    );
    const filteredUsers = users?.filter((user) => !isNameIndexOnly(user));
    const cardTypeId =
      typeClient === "C" ? CLIENT_TYPE_Id.CUSTOMER : CLIENT_TYPE_Id.SUPPLIER;
    const shouldIgnoreSearch = showTable;
    const updatedCustomer = {
      ...customer,
      contacts: filteredContacts,
      addresses: filteredAddresses,
      users: filteredUsers,
      cardTypeId: cardTypeId,
      isIgnoreSearch: shouldIgnoreSearch,

      currency: customer?.currency ?? systemCurrency,
      discountType: customer?.discountType ?? null,
    };

    if (hasDuplicateMainContactName(updatedCustomer, contactsForValidation)) {
      alertFault("customers.validation.mainContactNameMatchesContact");
      return;
    }

    // Check if email is valid
    if (filteredUsers && filteredUsers?.length > 0) {
      const areEmailsValid = filteredUsers?.every((user) =>
        validateEmail(user, "email"),
      );
      if (!areEmailsValid) {
        alertFault("customers.invalidEmail");
        return;
      }
    }

    setCustomer(updatedCustomer);

    const { isValid, missingBySection } = isValidCustomer(
      updatedCustomer,
      contactsForValidation,
      filteredAddresses,
      filteredUsers,
      customerRequiredFieldRules,
      {
        isSupplier: typeClient === CLIENT_TYPE.SUPPLIER,
        showCpaCode: CheckPermission(Permissions.SHOW_CPA_CODE),
      },
      addressFormIsUs,
    );
    const missingContactFirstNames =
      getMissingContactFirstNames(contactsForValidation);
    const alertSections = {
      ...missingBySection,
      contact: Array.from(
        new Set([
          ...missingBySection.contact,
          ...missingContactFirstNames,
        ]),
      ),
    };
    const hasMissingFields =
      !isValid ||
      alertSections.general.length > 0 ||
      alertSections.address.length > 0 ||
      alertSections.contact.length > 0;

    if (!hasMissingFields) {
      addNewCustomer(updatedCustomer).then((result: any) => {
        if (result?.isSimilarClientsFound) {
          setCustomerTableRows(result?.similarClients || []);
          setShowTable(true);
          alertFault("customers.similarClientsFoundConfirmAdd");
          return;
        }

        onCustomerAdd(result?.customer);
        handleClose();
        isFromCartPage && onChangeSelectBusiness(result?.customer);
      });
    } else {
      alertFault(formatCustomerMissingRequiredFieldsAlert(alertSections, t));
    }
  };

  const handleEditCustomer = () => {
    const filteredContacts = getContactsForSave();
    const contactsForValidation = getContactsForValidation(filteredContacts);
    const filteredAddresses = addresses?.filter(
      (address) => !isNameIndexOnly(address),
    );
    const filteredUsers = users?.filter((user) => !isNameIndexOnly(user));
    const updatedCustomer = {
      ...customer,
      contacts: filteredContacts,
      addresses: filteredAddresses,
      users: filteredUsers,
      currency: customer?.currency ?? systemCurrency,
      discountType: customer?.discountType ?? null,
    };

    // Check if email is valid
    if (filteredUsers && filteredUsers.length > 0) {
      const areEmailsValid = filteredUsers.every((user) =>
        validateEmail(user, "email"),
      );
      if (!areEmailsValid) {
        alertFault("customers.invalidEmail");
        return;
      }
    }

    const isClientEmailValid = validateEmail(customer, "mail");
    if (!isClientEmailValid) {
      alertFault("customers.invalidEmail");
      return;
    }

    setCustomer(updatedCustomer);

    const { isValid, missingBySection } = isValidCustomer(
      updatedCustomer,
      contactsForValidation,
      filteredAddresses,
      filteredUsers,
      customerRequiredFieldRules,
      {
        isSupplier: typeClient === CLIENT_TYPE.SUPPLIER,
        showCpaCode: CheckPermission(Permissions.SHOW_CPA_CODE),
      },
      addressFormIsUs,
    );
    const missingContactFirstNames =
      getMissingContactFirstNames(contactsForValidation);
    const alertSections = {
      ...missingBySection,
      contact: Array.from(
        new Set([
          ...missingBySection.contact,
          ...missingContactFirstNames,
        ]),
      ),
    };
    const hasMissingFields =
      !isValid ||
      alertSections.general.length > 0 ||
      alertSections.address.length > 0 ||
      alertSections.contact.length > 0;

    if (!hasMissingFields) {
      editCustomer(updatedCustomer, setCustomer).then((x) => {
        if (isgetAllCustomers) {
          getAllCustomers();
        }
        handleClose();
      });
    } else {
      alertFault(formatCustomerMissingRequiredFieldsAlert(alertSections, t));
    }
  };

  const [contacts, setContacts] = useState(() => {
    if (customer && customer.contacts) {
      return customer.contacts.map((contact, i) => ({
        ...contact,
        index: contact.index ?? i + 1,
      }));
    }
    return [];
  });
  const [addresses, setAddresses] = useState(() => {
    if (customer && customer.addresses) {
      return customer.addresses.map((address, i) => ({
        ...address,
        index: address.index ?? i + 1,
      }));
    }
    return [];
  });
  const [users, setUsers] = useState(() => {
    if (customer && customer.users) {
      return customer.users.map((user, i) => ({
        ...user,
        index: user.index ?? i + 1,
      }));
    }
    return [];
  });

  // Contact info
  const addEmptyContact = () => {
    if (!contacts || contacts === undefined) {
      addInitContact();
    } else {
      var temp = [...contacts];
      const index = temp.length + 1;
      temp.push({ name: "", index: index });
      setContacts(temp);
    }
  };

  const addInitContact = () => {
    var temp = [];
    if (customer && customer.contacts) {
      temp = customer.contacts.map((contact, i) => ({
        ...contact,
        index: contact.index ?? i + 1,
      }));
    }

    // else {
    //   const index = temp.length + 1;
    //   temp.push({ name: "", index: index });
    // }
    setContacts(temp);
  };

  const deleteContactForm = (index) => {
    var temp = [...contacts];
    temp = temp?.filter((x) => x.index != index);

    temp.forEach((contact, i) => {
      if (contact?.index > index) {
        contact.index -= 1;
      }
    });
    setContacts(temp);
  };

  const updateContact = (index, updatedContactData) => {
    setContacts((prevContacts) =>
      prevContacts.map((contact) =>
        contact.index === index
          ? { ...contact, ...updatedContactData }
          : contact,
      ),
    );
  };

  // Address info
  const addEmptyAddress = () => {
    if (!addresses || addresses === undefined) {
      addInitAddress();
    } else {
      var temp = [...addresses];
      const index = temp.length + 1;
      const isFirstAddress = temp.length === 0;
      temp.push({
        index: index,
        country: printHouseCountry,
        type: EAddressType.DELIVERY,
        isDefault: isFirstAddress,
      });
      setAddresses(temp);
    }
  };

  const addInitAddress = () => {
    var temp = [];
    if (customer && customer.addresses) {
      temp = customer.addresses.map((address, i) => ({
        ...address,
        index: address.index ?? i + 1,
      }));
    }
    // else {
    //   const index = temp.length + 1;
    //   temp.push({ name: "", index: index });
    // }
    setAddresses(temp);
  };

  const deleteAddressForm = (index) => {
    var temp = [...addresses];
    temp = temp?.filter((x) => x.index != index);
    temp.forEach((address, i) => {
      if (address.index > index) {
        address.index -= 1;
      }
    });
    setAddresses(temp);
  };

  const updateAddress = (index, updatedAddressData) => {
    setAddresses((prevAddresses) => {
      const updatedList = prevAddresses?.map((address) => {
        if (address?.index === index) {
          return { ...address, ...updatedAddressData };
        }
        return address;
      });

      const updatedAddress = updatedList.find((a) => a.index === index);

      // If isDefault was set to true, reset others of same type ( billing/delivery)
      if (updatedAddressData.isDefault) {
        return updatedList.map((address) => {
          if (address.index !== index && address.type === updatedAddress.type) {
            return { ...address, isDefault: false };
          }
          return address;
        });
      }

      return updatedList;
    });
  };

  // User info
  const addEmptyClient = () => {
    var temp = [...users];
    const index = temp.length + 1;
    temp.push({
      name: "",
      index: index,
      systemLanguage: customer?.clientLang || null,
    });
    setUsers(temp);
  };

  const addInitUser = () => {
    var temp = [];
    if (customer && customer.users) {
      temp = customer.users.map((user, i) => ({
        ...user,
        index: user.index ?? i + 1,
      }));
    }
    setUsers(temp);
  };

  const deleteUserForm = (index) => {
    var temp = [...users];
    temp = temp?.filter((x) => x.index != index);
    temp.forEach((user, i) => {
      if (user.index > index) {
        user.index -= 1;
      }
    });
    setUsers(temp);
  };

  const updateUser = (index, updatedUserData) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.index === index ? { ...user, ...updatedUserData } : user,
      ),
    );
  };

  const onChangeInputs = (key, value) => {
    const newValue = value === "" ? null : value;

    if (showTable && similarCustomerSearchKeys.includes(key)) {
      setShowTable(false);
    }

    setCustomer((prev) => ({ ...prev, [key]: newValue }));
  };

  const onUpdatePass = async (
    currentPass: any,
    newPass: any,
    confirmPass: any,
  ) => {
    const res = await updateUserPassword(
      currentPass,
      newPass,
      confirmPass,
      gomakeUser?.id,
    );
    setResetPassModalModal(!res);
  };

  const [isClientType, setClientType] = useState(false);
  const onClickCloseClientType = () => {
    setClientType(false);
  };
  const onClickOpenClientType = () => {
    setClientType(true);
  };

  const [clientGroupList, setClientGroupList] = useRecoilState(
    clientGroupsCategoriesState,
  );
  const paymentTermsState =
    typeClient === CLIENT_TYPE.SUPPLIER
      ? paymentTermsSupplierState
      : paymentTermsCustomerState;
  const cardTypeId =
    typeClient === CLIENT_TYPE.SUPPLIER
      ? CLIENT_TYPE_Id.SUPPLIER
      : CLIENT_TYPE_Id.CUSTOMER;

  const [paymentTermsCategories, setPaymentTermsCategories] = useRecoilState(
    paymentTermsState,
  );

  const getAllPaymentTerms = async () => {
    const callBack = (res) => {
      if (res?.success) {
        setPaymentTermsCategories(res?.data ?? []);
      }
    };
    await getAllPaymentTermsApi(callApi, callBack, { cardType: cardTypeId });
  };

  const getClientsGroups = async () => {
    const callBack = (res) => {
      if (res?.success) {
        setClientGroupList(res?.data);
      }
    };
    await getClientsGroupsApi(callApi, callBack);
  };

  useEffect(() => {
    if (openModal) {
      getClientsGroups();
      getAllPaymentTerms();
    }
  }, [openModal, typeClient]);

  const getAllProducts = useCallback(async () => {
    const products = await getAllProductsMongoDB(callApi);
    if (Array.isArray(products)) {
      const flattened: { id: string; name: string }[] = [];
      products.forEach((product: any) => {
        const productId = product?.id || product?.Id;
        const productName = product?.name || product?.Name;
        if (productId && productName) {
          flattened.push({ id: productId, name: productName });
        }
        const subProducts = product?.subProducts || product?.SubProducts || [];
        subProducts.forEach((sub: any) => {
          const subId = sub?.id || sub?.Id;
          const subName = sub?.name || sub?.Name;
          if (subId && subName) {
            flattened.push({ id: subId, name: subName });
          }
        });
      });
      setProductsList(flattened);
    }
  }, [callApi]);

  useEffect(() => {
    if (openModal && typeClient === CLIENT_TYPE.CUSTOMER) {
      getAllProducts();
    }
  }, [openModal, typeClient, getAllProducts]);

  const [newGroupId, setNewGroupId] = useState("");
  const addGroupName = useCallback(async (data: string) => {
    const res: any = await callApi(
      "POST",
      "/v1/crm-service/customer/add-client-group",
      {
        groupName: data,
      },
    );
    if (res?.success) {
      getClientsGroups();
      setNewGroupId(res?.data?.data?.data);
    } else {
    }
  }, []);

  const defaultPaymentTerm = GetPreferenceKey(
    EPreferences.DEFAULT_PAYMENT_TERMS,
  );

  return {
    customerTableHeaders,
    customerTableColumnWidths,
    customerTableRows,
    showTable,
    handleHideTable,
    setCustomerTableRows,
    getAllSimilarCustomersData,
    onShowInActiveCustomers,
    mapCustomerData,
    setShowInActiveCustomers,
    setShowTable,
    users,
    setUsers,
    addresses,
    setAddresses,
    contacts,
    setContacts,
    addInitUser,
    addInitAddress,
    addInitContact,
    deleteUserForm,
    deleteContactForm,
    deleteAddressForm,
    updateContact,
    updateAddress,
    updateUser,
    addEmptyContact,
    addEmptyClient,
    addEmptyAddress,
    open,
    setOpen,
    handleEditCustomer,
    handleAddCustomer,
    isNameIndexOnly,
    validateEmail,
    alertFault,
    clientTypeId,
    clientTypeLabel,
    setResetPassModalModal,
    resetPassModal,
    clientTypesCategories,
    handleClose,
    languagesOptions: defaultLanguageOptions,
    gomakeUser,
    isClientType,
    onClickCloseClientType,
    onClickOpenClientType,
    updateUserPassword,
    onUpdatePass,
    onChangeInputs,
    clientGroupList,
    addGroupName,
    newGroupId,
    setNewGroupId,
    CheckPermission,
    profileState,
    systemCurrency,
    defaultPaymentTerm,
    getAllShipmentTypes,
    shipmentTypes,
    primaryColor,
    secondaryColor,
    productsList,
    customerRequiredFieldRules,
    addressFormIsUs,
    paymentTermsCategories,
  };
};

export { useCustomerCard };
